import { IconButton, Typography } from '@clef/client-library';
import { GroupUser, PendingUser, User, UserStatus } from '@clef/shared/types';
import { emptyGroups, isGroupUser, isUser } from '@clef/shared/utils';
import { Box, Grid, makeStyles, Tooltip } from '@material-ui/core';
import React, { useMemo, useState } from 'react';

import { Column as UserColumn } from '@/components/Layout/components/Column';
import { UserAvatar } from '@/components/UserAvatar/UserAvatarNew';
import { Chip } from './Chip';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { useGetGroupsQuery } from '@/serverStore/account/queries';
import GroupMembersDialog from '../Members/GroupMembersDialog';
import MoreHoriz from '@material-ui/icons/MoreHoriz';
import GroupIcon from '@material-ui/icons/Group';

export type DataItem = User | PendingUser | GroupUser;

export type UserStatusShow = 'always' | 'inactive-only' | 'never';

const useUserProfileStyles = makeStyles(theme => {
  return {
    userInfoGrid: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    avatar: {
      minWidth: 30,
    },
    userInfoColumn: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(5),
    },
    usernameRow: {
      display: 'flex',
    },
    userNameText: {
      fontWeight: 'bold',
    },
    userEmailText: {
      color: theme.palette.greyModern[500],
    },
    members: {
      '&:hover $viewMembersButton': {
        opacity: 1,
        visibility: 'visible',
      },
    },
    viewMembersButton: {
      marginLeft: theme.spacing(1),
      opacity: 0,
      transition: 'opacity 0.2s ease-in-out',
      visibility: 'hidden',
    },
    groupIcon: {
      marginRight: theme.spacing(2),
    },
  };
});

export const getUserName = (item: DataItem) => {
  return isUser(item) || isGroupUser(item)
    ? [item.name, item.lastName].filter(Boolean).join(' ')
    : t('Pending user');
};

const UserProfile = (props: { item: DataItem; showUserStatus?: UserStatusShow }) => {
  const { item, showUserStatus = 'never' } = props;
  const shouldShowUserStatus =
    showUserStatus === 'always' ||
    (showUserStatus === 'inactive-only' && (item as User)['status'] === UserStatus.Inactive);
  const userName = getUserName(item);
  const styles = useUserProfileStyles();
  const renderUserAvatar = (item: DataItem) => {
    const userName = getUserName(item);
    return (
      <UserAvatar
        name={userName || item.email}
        disableTooltip
        classes={{
          avatar: styles.avatar,
        }}
      />
    );
  };
  const loginUser = useTypedSelector(state => state.login.user!);
  const [selectedGroup, setSelectedGroup] = useState<GroupUser | null | undefined>(null);
  const { data: groups = emptyGroups } = useGetGroupsQuery({ isEnabled: loginUser.orgEnableGroup });

  const membersNamesComponent = useMemo(() => {
    const groupsById = groups.reduce((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {} as Record<string, GroupUser>);

    if (!isGroupUser(item) || !(item.id in groupsById)) {
      return null;
    }
    const { members } = groupsById[item.id]!;
    if (!members) {
      return (
        <Tooltip title={t('Only group members can see the member list')}>
          <span>--</span>
        </Tooltip>
      );
    }

    let memberNames = '';
    const firstMemberNames = members
      .slice(0, 3)
      .map(item => [item.name, item.lastName].filter(Boolean).join(' '));
    const numberOfRestMembers = members.length - 3;
    if (members.length === 0) {
      memberNames = t('No members');
    } else if (members.length <= 3) {
      memberNames = firstMemberNames.join(', ');
    } else {
      memberNames = t('{{firstMemberNames}} and {{numberOfRestMembers}} others', {
        firstMemberNames: firstMemberNames.join(', '),
        numberOfRestMembers,
      });
    }
    return (
      <Box display="flex" alignItems="center" className={styles.members}>
        <span>{memberNames}</span>
        {numberOfRestMembers > 0 && (
          <Tooltip title={t('View Members')}>
            <IconButton
              id={`org-settings-groups-table-member-button-${item.id}`}
              size="small"
              color="default"
              className={styles.viewMembersButton}
              onClick={() => setSelectedGroup(item)}
            >
              <MoreHoriz />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [item, groups, styles]);

  return (
    <Grid item className={styles.userInfoGrid}>
      {renderUserAvatar(item)}
      <UserColumn className={styles.userInfoColumn}>
        <div className={styles.usernameRow}>
          <Box display="flex" alignItems="center">
            {isGroupUser(item) && <GroupIcon fontSize="small" className={styles.groupIcon} />}
            <Typography variant="body2" className={styles.userNameText}>
              {userName}
            </Typography>
          </Box>
          {shouldShowUserStatus && <Chip color="yellow" text={t('Inactive')} />}
        </div>
        <Typography variant="body2" className={styles.userEmailText}>
          {membersNamesComponent ?? item.email}
        </Typography>
      </UserColumn>
      <GroupMembersDialog group={selectedGroup} onClose={() => setSelectedGroup(null)} />
    </Grid>
  );
};

export default UserProfile;
